import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import type { MessagingConnection } from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import { ConnectionCard } from '../components/notifications/connection-card.js';
import { Badge, ListSkeleton, useToast } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useModuleAccess } from '../lib/billing/entitlements.js';
import { INTEGRATIONS, type IntegrationDefinition } from '../lib/integrations/registry.js';
import {
  useDisconnectWorkspace,
  useMessageLog,
  useMessagingSettings,
  useSaveConnection,
  useTestConnection,
} from '../lib/notifications/use-notifications.js';

/**
 * The outside services the shop is wired to, one card each.
 *
 * The screen exists because "is WhatsApp on?" is a different question from "what do we tell
 * customers, and when?" — the first is asked once while setting the shop up and again the
 * morning nothing arrives, the second is edited over months. Putting the connection here
 * leaves the notifications screen to be about messages.
 *
 * Every plan can REACH this page. What the plan decides is what the card offers, which is why
 * the lock lives on the card and not on the route: a shop that has downgraded still has a
 * workspace and a history, and a screen that 404s would read as having lost them.
 */
export function IntegrationsPage(): ReactElement {
  const { t } = useTranslation();
  const settings = useMessagingSettings();

  if (settings.isPending) {
    return (
      <>
        <PageHeader title={t('integrations.title')} description={t('integrations.description')} />
        <ListSkeleton rows={2} label={t('common.loading')} />
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('integrations.title')} description={t('integrations.description')} />

      <div className="flex flex-col gap-6">
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 self-start text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
          {t('integrations.back')}
        </Link>

        {INTEGRATIONS.map((integration) => (
          <IntegrationCard
            key={integration.key}
            integration={integration}
            connection={settings.data?.connection}
          />
        ))}
      </div>
    </>
  );
}

/**
 * One registry entry, rendered as whatever that integration actually needs.
 *
 * The switch has no `default` that throws. A key this build does not know how to draw is a
 * missing card, not a broken settings screen — and because {@link IntegrationDefinition} keys
 * are a union rather than `string`, the next integration cannot reach this fallthrough without
 * first failing to compile here.
 */
function IntegrationCard({
  integration,
  connection,
}: {
  integration: IntegrationDefinition;
  connection: MessagingConnection | undefined;
}): ReactElement | null {
  switch (integration.key) {
    case 'whatsapp':
      return <WhatsAppCard definition={integration} connection={connection} />;
    default:
      return null;
  }
}

/**
 * WhatsApp: who it is, whether the shop may have it, and — if it does — the workspace itself.
 *
 * The connection card underneath is the one the notifications screen already used, unchanged.
 * Everything this component adds is context around it: whose service it is, whether the plan
 * includes it, and how it has been behaving.
 *
 * Two things decide whether the connect controls appear, and they are decided in different
 * places on purpose. The SHOP comes from `moduleAccess` — the same function the API's plan
 * gate refuses with — rather than from "is the module in the plan?", because those two answer
 * differently for a shop that has the module and has stopped paying for it: plan-membership
 * says yes, the gate says `read_only`, and the shop is handed controls that 402. The PERSON
 * comes from the session's role, the way the billing screen reads it: a manager holds
 * `settings.read` and not `settings.notifications.write`, so every write they could start
 * would come back a 403. Either answer produces the same read-only card — every fact, no
 * controls — but they are explained differently, because "buy this" and "pay your bill" and
 * "ask your owner" are three different things to be told.
 */
function WhatsAppCard({
  definition,
  connection,
}: {
  definition: IntegrationDefinition;
  connection: MessagingConnection | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { role } = useAuth();
  const access = useModuleAccess(definition.module);

  const saveConnection = useSaveConnection();
  const testConnection = useTestConnection();
  const disconnect = useDisconnectWorkspace();

  const { icon: Icon } = definition;
  // `null` only before a shop is in the session; nothing is locked on a guess, and the API is
  // still the one refusing.
  const lockedByPlan = access !== null && access.mode !== 'full';
  const notInPlan = access?.reason === 'module_not_in_plan';
  const behindOnPayment = access?.reason === 'subscription_read_only';
  const readOnly = lockedByPlan || role !== 'owner';
  const connected = connection !== undefined && connection.status !== 'not_connected';
  // `notifications.log.read` is refused to tailors. Rendering the line anyway would turn two
  // 403s into "0 messages sent, 0 failed" — a shop told its messaging is idle by the one
  // screen it went to to check.
  const canReadLog = role !== 'tailor';

  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Icon aria-hidden="true" className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="flex flex-wrap items-center gap-2 font-medium">
            {t(definition.titleKey)}
            {notInPlan ? <Badge variant="secondary">{t('settings.notInPlan')}</Badge> : null}
          </h3>
          <p className="text-sm text-muted-foreground">{t(definition.providerKey)}</p>
          <p className="text-sm text-muted-foreground">{t(definition.descriptionKey)}</p>
        </div>
      </header>

      {/* The way out of the lock, beside the badge that explains it. Which way out depends on
          which lock: a plan that never included this is bought, an unpaid one is settled. */}
      {notInPlan ? (
        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {t('integrations.upgrade')}{' '}
          <Link to="/settings/billing" className="font-medium text-foreground underline">
            {t('billing.title')}
          </Link>
        </p>
      ) : null}

      {behindOnPayment ? (
        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {t('billing.readOnlyNotice')}{' '}
          <Link to="/settings/billing" className="font-medium text-foreground underline">
            {t('billing.title')}
          </Link>
        </p>
      ) : null}

      {connection ? (
        readOnly ? (
          <ConnectionCard
            connection={connection}
            readOnly
            /* The card's own sentence — "an owner of this shop can connect it from this
               screen" — is true of a manager's view and false under a lock, where no owner
               can either. Whoever is being refused is told why by the same paragraph. */
            emptyMessage={
              lockedByPlan
                ? t('integrations.whatsapp.lockedEmpty')
                : t('notifications.connectionReadOnlyEmpty')
            }
          />
        ) : (
          <ConnectionCard
            connection={connection}
            saving={saveConnection.isPending}
            testing={testConnection.isPending}
            onSave={async (apiKey) => {
              const saved = await saveConnection.mutateAsync({ apiKey });
              toast(
                saved.status === 'connected'
                  ? { title: t('notifications.connected'), variant: 'success' }
                  : {
                      title: t('notifications.connectionFailed'),
                      description: saved.lastError ?? undefined,
                      variant: 'destructive',
                    },
              );
            }}
            onTest={async () => {
              const tested = await testConnection.mutateAsync();
              toast(
                tested.status === 'connected'
                  ? { title: t('notifications.connectionWorking'), variant: 'success' }
                  : {
                      title: t('notifications.connectionFailed'),
                      description: tested.lastError ?? undefined,
                      variant: 'destructive',
                    },
              );
            }}
            onDisconnect={async () => {
              await disconnect.mutateAsync();
              toast({ title: t('notifications.disconnected'), variant: 'success' });
            }}
          />
        )
      ) : null}

      {/* A shop that has never connected has nothing to report, and a line of zeroes would
          read as a failure rather than as an absence. */}
      {connected ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          {canReadLog ? <WhatsAppHealth /> : null}
          {/* Not inside the health line: the event settings are readable by everyone who can
              reach this screen, including the tailor the message log is closed to. */}
          <Link to="/settings/notifications#notification-events" className="font-medium underline">
            {t('integrations.whatsapp.manage')}
          </Link>
        </div>
      ) : null}
    </section>
  );
}

/**
 * How the shop's messaging has actually been behaving, in one line.
 *
 * Its own component so that its two queries only run for somebody who is allowed to see the
 * answer — an unmounted component asks for nothing, which is how the tailor who cannot read
 * the log avoids asking for it twice and being refused twice.
 *
 * The counts come out of the message log the app already reads, asked for one row at a time:
 * the log's page carries the TOTAL matching the filter, so "how many sent" and "how many
 * failed" are two cheap reads of an existing endpoint rather than a new one. The dead-letter
 * count rides along on the same response, and when it is anything but zero it is said loudly,
 * because those are messages a customer never got.
 *
 * Both reads have to have ARRIVED before the line is drawn. `?? 0` on a request that failed
 * or has not landed yet prints a confident "0 messages sent, 0 failed" over the top of a shop
 * that has sent thousands, and a number nobody can tell apart from a measurement is worse
 * than a line that is not there yet.
 */
function WhatsAppHealth(): ReactElement | null {
  const { t } = useTranslation();
  const all = useMessageLog({ pageSize: 1 });
  const failed = useMessageLog({ status: 'failed', pageSize: 1 });

  if (!all.data || !failed.data) return null;

  const deadLetterCount = all.data.deadLetterCount;

  return (
    <>
      <p className="text-muted-foreground">
        {t('integrations.whatsapp.health', {
          sent: all.data.total,
          failed: failed.data.total,
        })}
      </p>

      {deadLetterCount > 0 ? (
        <Badge variant="destructive">
          {t('notifications.deadLetterCount', { count: deadLetterCount })}
        </Badge>
      ) : null}

      <Link to="/settings/notifications#message-log" className="font-medium underline">
        {t('integrations.whatsapp.viewLog')}
      </Link>
    </>
  );
}

export default IntegrationsPage;
